/*
 * Iter312 — Rally-Lab Festival Licence — plain-English terms page.
 *
 * Linked from the organiser signup checkbox and from the basket checkout
 * confirmation. Written in short, human sentences on purpose — legalese
 * hides the important rules from the person clicking "I agree".
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Zap } from "lucide-react";

const ACCENT = "#FF4500";

const CLAUSES = [
  {
    title: "Your login is yours alone",
    body:
      "Your Rally-Lab organiser email + password are personal and non-transferable. Sharing them with anyone — a colleague, a helper, a friend — is a breach of this licence and can trigger immediate account termination. If a teammate needs access, use a Helper Pass instead (Festival → Helpers).",
  },
  {
    title: "One device at a time",
    body:
      "Only one active session is permitted per organiser account. Signing in on a new device automatically signs you out on your previous one. You'll see a message telling you that has happened. This is how we make sure a paid seat is a paid seat.",
  },
  {
    title: "Your internet, your responsibility",
    body:
      "You are solely responsible for ensuring a stable internet connection during your event. Rally-Lab accepts no liability for disruption caused by connectivity issues — dropped Wi-Fi, spotty 4G, venue outages, or anything else outside our servers.",
  },
  {
    title: "Use at your own risk",
    body:
      "Rally-Lab accepts no liability for any loss, disruption, or damages arising from your use of this tool — direct, indirect, incidental or consequential. We work hard to keep the platform up and honest, but running the event on the day is on you.",
  },
  {
    title: "One licence, one organiser, no refunds after activation",
    body:
      "Each licence is issued to the named organiser at checkout and cannot be transferred to another person or venue. Licences are non-refundable once activated — clicking Activate on your festival is the point of no return, so double-check the details first. If something's wrong before you activate, get in touch and we'll sort it.",
  },
];

export default function FestivalTerms() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg" data-testid="festival-terms-page">
      <header className="border-b border-[#262626]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center" style={{ background: ACCENT }}>
              <Zap strokeWidth={2.5} className="w-4 h-4 text-black" />
            </div>
            <span className="h-display text-lg tracking-tighter">RALLY LAB</span>
          </Link>
          <Link
            to="/festival"
            data-testid="terms-back-to-festival"
            className="text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Festival
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-10">
        <div>
          <div className="label-mono mb-2" style={{ color: ACCENT }}>FESTIVAL LICENCE · V1</div>
          <h1 className="h-display text-4xl sm:text-5xl tracking-tighter leading-none">
            The short, honest rules for using Rally-Lab Festival.
          </h1>
          <p className="mt-4 text-sm text-[#A3A3A3] max-w-2xl">
            No small print. Five rules that keep the tool affordable for organisers who play fair
            and out of the hands of accounts trying to share seats. Read them, tick the box, run
            your event.
          </p>
        </div>

        <ol className="space-y-6 counter-reset">
          {CLAUSES.map((c, i) => (
            <li key={c.title} className="card-surface p-5" data-testid={`terms-clause-${i + 1}`}>
              <div className="flex items-start gap-4">
                <div
                  className="shrink-0 w-9 h-9 flex items-center justify-center text-sm font-bold"
                  style={{ background: ACCENT, color: "#0A0A0A" }}
                >
                  {i + 1}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white mb-1">{c.title}</h2>
                  <p className="text-sm text-[#A3A3A3] leading-relaxed">{c.body}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>

        <div className="border border-[#262626] p-5 text-xs text-[#A3A3A3] leading-relaxed">
          Rally-Lab is provided "as is". By creating an organiser account or completing a
          licence purchase, you confirm you have read and agree to the five rules above.
          Questions? Reach us any time via the enquiry form on the pricing page and we'll
          come back to you within 24-72 hours.
        </div>

        <div className="text-center">
          <Link
            to="/register?as=organiser"
            data-testid="terms-create-account"
            className="inline-block px-6 py-3 text-sm font-bold uppercase tracking-wider"
            style={{ background: ACCENT, color: "#0A0A0A" }}
          >
            Create organiser account
          </Link>
        </div>
      </main>
    </div>
  );
}
